import React, { useEffect, useMemo } from 'react'
import { useField } from 'formik'
import { faCheck, faCircleQuestion } from '@fortawesome/pro-regular-svg-icons'

import { Button, Icon, Text, Tooltip } from '@/ui'
import { mtc, TextColors, TypographyStyles } from '@/styles'
import { useReselector } from '@/utils/sharedHooks'

import type { IntakeAnswers } from '../lib/types'
import { selectLicenseOptions } from '../compliance.selectors'
import { INTAKE_QUESTION_CARD_CLS } from '../lib/constants'
import type { QuestionDef } from '../questions/types'
import {
  IncomeBandSliderField,
  MultiSelect,
  NumberField,
  PerStateDateField,
  PerStateSelect,
  RecentPastDateField,
  SingleSelect,
  StateSelect,
  TextField,
  YearMonthDateField,
} from './inputs'

/** '' → null for a date value (or every value in a per-state date map) — the
 *  date picker's clear button writes '' through Formik, but the backend's
 *  date schema accepts a real date or null, never ''. */
const sanitizeDateValue = (value: unknown): unknown => {
  if (value === '') return null
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>).map(([k, v]) => [
        k,
        v === '' ? null : v,
      ])
    )
  }
  return value
}

/**
 * The list shape a `prepopulateFrom` source has to be in before it can seed a
 * multi-select.
 *
 * The source may itself be a multi-select (`states`) or a single one
 * (`state`): `licensed_states` copies `entity_registration_state`, which
 * stores one bare state code. Passing that through unwrapped would let the
 * seeding spread it into its characters.
 */
export const prepopulateValues = (source: unknown): string[] | undefined => {
  if (Array.isArray(source)) return source as string[]
  if (typeof source === 'string' && source) return [source]
  return undefined
}

/** The card a question sits in when it's rendered as its own row in the
 *  intake column. */
const CARD_CLS = `${INTAKE_QUESTION_CARD_CLS} p-4`

/**
 * Renders a single question from its `QuestionDef`, dispatching on `def.input`.
 * The DS Form.* molecules (used by the `./inputs` renderers below) read/write
 * this question's value through Formik, keyed by `def.key` — `onChange` mirrors
 * that value into Redux (the flow's source of truth for section navigation,
 * reveal logic, and submission) whenever Formik's copy changes.
 */
export const IntakeQuestion: React.FC<{
  def: QuestionDef
  allAnswers: IntakeAnswers
  onAnswer: (key: string, value: unknown) => void
  /** Whether to show this question's "needs an answer" message — decided by the
   *  section, which owns both the answers map and whether the user has tried to
   *  continue past an incomplete section. */
  hasError?: boolean
  /** Called when the user reaches into this question — clicks or tabs into
   *  one of its controls. Tells the section which question is being worked on,
   *  so it can leave that one open and close the rest. Deliberately driven by
   *  the user touching the question rather than by its value changing: some
   *  fields write themselves (the state picker seeds itself from an earlier
   *  answer), and a question filling itself in is not the user moving on. */
  onInteract?: (key: string) => void
  /** Renders the question in its own card (see `CARD_CLS`). Opt-in: the
   *  intake flow wants it; the plan-pricing dialog, which reuses this
   *  component inside a narrow modal, already has a surface of its own. */
  asCard?: boolean
  /** Renders a "Done editing" control in the card's top-right corner. Passed
   *  only for a question the user reopened after it had already collapsed;
   *  everything else settles on its own. */
  onDoneEditing?: () => void
  /** Renders a "Confirm" button under the control, for a question that has no
   *  natural end of its own — see `requiresConfirmation`. */
  onConfirm?: () => void
  /** Renders that button inert while there is nothing to confirm. */
  confirmDisabled?: boolean
}> = ({
  def,
  allAnswers,
  onAnswer,
  onInteract,
  hasError,
  asCard,
  onDoneEditing,
  onConfirm,
  confirmDisabled,
}) => {
  const [field] = useField(def.key)
  useEffect(() => {
    // Only Redux (what actually gets submitted) needs the '' → null cleanup;
    // Formik's own copy is left alone since '' displays identically to unset.
    const isDateShaped = def.input === 'date' || def.input === 'per_state_date'
    onAnswer(
      def.key,
      isDateShaped ? sanitizeDateValue(field.value) : field.value
    )
    // Only the field's own value should re-trigger the Redux mirror — `onAnswer`
    // is a fresh closure every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [field.value])

  const prepopulateSource = def.prepopulateFrom
    ? allAnswers[def.prepopulateFrom]
    : undefined
  // Memoized on the raw answer, which is a stable reference from Redux.
  // Without this, a single-value source builds a fresh array every render, and
  // the seeding effect downstream keys off that identity, so it would re-run,
  // write, re-render, and loop until React gives up.
  const prepopulateFrom = useMemo(
    () => prepopulateValues(prepopulateSource),
    [prepopulateSource]
  )

  const licenseOptions = useReselector(selectLicenseOptions)
  // Constrain the per-state credential picker to each state's valid options
  // (from the backend catalog) so an invalid state/credential can't be submitted.
  const licenseOptionsByState = Object.fromEntries(
    Object.entries(licenseOptions).map(([state, opts]) => [
      state,
      opts.map((o) => ({ value: o.code, label: o.label })),
    ])
  )
  const control = ((): React.ReactNode => {
    switch (def.input) {
      case 'single':
        return (
          <SingleSelect
            name={def.key}
            options={def.options ?? []}
            columns={def.columns}
            icon={def.optionIcon}
            badge={def.optionBadge}
            compact={def.compact}
          />
        )
      case 'multi':
        return (
          <MultiSelect
            name={def.key}
            options={def.options ?? []}
            columns={def.columns}
          />
        )
      case 'states':
        return (
          <StateSelect name={def.key} multi prepopulateFrom={prepopulateFrom} />
        )
      case 'state':
        return <StateSelect name={def.key} multi={false} />
      case 'date':
        return def.recentPastDate ? (
          <RecentPastDateField name={def.key} />
        ) : (
          <YearMonthDateField name={def.key} bound={def.dateBound} />
        )
      case 'per_state_date':
        return (
          <PerStateDateField
            name={def.key}
            states={(allAnswers.licensed_states as string[] | undefined) ?? []}
            bound={def.dateBound}
          />
        )
      case 'per_state_select':
        return (
          <PerStateSelect
            name={def.key}
            states={(allAnswers.licensed_states as string[] | undefined) ?? []}
            options={def.options ?? []}
            optionsByState={licenseOptionsByState}
          />
        )
      case 'number':
        return (
          <NumberField
            name={def.key}
            unit={def.unit}
            slider={def.numberSlider}
          />
        )
      case 'income_band_slider':
        return <IncomeBandSliderField name={def.key} />
      case 'tel':
        return <TextField name={def.key} type="tel" />
      case 'text':
        return <TextField name={def.key} />
      default:
        return null
    }
  })()

  // A question is a group of controls (option cards, a per-state row set, a
  // year/month pair) named by its prompt — without the grouping, each control
  // announces alone and the prompt is unassociated text above it.
  const promptId = `question-${def.key}-prompt`
  return (
    <div
      id={`question-${def.key}`}
      role="group"
      aria-labelledby={promptId}
      // `text-base` on every text input below `sm`: iOS Safari zooms the page in whenever
      // a focused input's font size is under 16px, and it does not zoom back out on blur —
      // so tapping the state search left the reader stranded at 1.3x. Set here rather than
      // on the DS atoms, which are shared with the rest of the app. Radix `Select`
      // triggers are buttons, not inputs, so this only reaches the fields that can zoom.
      className={mtc(
        'flex flex-col gap-2 max-sm:[&_input]:text-base',
        asCard && CARD_CLS
      )}
      // Capture phase, so a control that stops the event still registers, and
      // both events because a control can be reached by pointer or by tab.
      // A pointer press on a select's trigger lands here before its popover
      // opens, so a later click on an option — portalled outside this group —
      // doesn't need to be seen at all.
      onPointerDownCapture={() => onInteract?.(def.key)}
      onFocusCapture={() => onInteract?.(def.key)}
    >
      {/* `flex-col-reverse` below `sm`, so "Done Editing" — the later sibling — sits above
          the prompt and left-aligns with it. Beside a prompt at phone width it took the
          room the prompt needed and wrapped the question into a narrow column. */}
      <div className="flex flex-col-reverse items-start gap-2 sm:flex-row sm:items-start sm:justify-between sm:gap-3">
        <div className="flex flex-col gap-0.5">
          {/* Centered rather than baseline-aligned: the tooltip trigger is an
              icon, and an icon sitting on a text baseline reads as dropped.
              The size gap between the prompt and "(optional)" is small enough
              that centering the two costs nothing. */}
          <div className="flex items-center">
            {/* The prompt and its "(optional)" marker keep a gap between them;
                the help icon does not, so it reads as attached to the prompt
                rather than as a third item in a list. */}
            <div className="flex items-center gap-1">
              <Text id={promptId} variant={TypographyStyles.LABEL_SM}>
                {def.prompt}
              </Text>
              {def.optional && (
                <Text
                  variant={TypographyStyles.BODY_XS}
                  color={TextColors.MUTED}
                >
                  (optional)
                </Text>
              )}
            </div>
            {/* Pointer devices only — Radix tooltips don't open on touch, so the
                same copy renders inline below `sm`. */}
            {def.help && (
              <Tooltip>
                <Tooltip.Trigger asChild>
                  {/* A real button, so the reason is reachable by keyboard and
                      announced, rather than hover-only decoration. */}
                  <button
                    type="button"
                    aria-label={`Why we ask: ${def.prompt}`}
                    className="hidden shrink-0 items-center leading-none sm:flex"
                  >
                    <Icon
                      icon={faCircleQuestion}
                      size="sm"
                      color="text-neutral-400"
                    />
                  </button>
                </Tooltip.Trigger>
                <Tooltip.Content className="max-w-64">
                  <Text variant={TypographyStyles.BODY_SM}>{def.help}</Text>
                </Tooltip.Content>
              </Tooltip>
            )}
          </div>
          {def.description && (
            <Text variant={TypographyStyles.BODY_XS} color={TextColors.MUTED}>
              {def.description}
            </Text>
          )}
          {def.help && (
            <Text
              variant={TypographyStyles.BODY_XS}
              color={TextColors.MUTED}
              className="sm:hidden"
            >
              {def.help}
            </Text>
          )}
        </div>
        {onDoneEditing && (
          <button type="button" onClick={onDoneEditing} className="shrink-0">
            <Text
              variant={TypographyStyles.BODY_XS}
              color={TextColors.MUTED}
              className="underline"
            >
              Done Editing
            </Text>
          </button>
        )}
      </div>
      {onConfirm ? (
        // Inline, to the control's right. Under it, Confirm read as a
        // separate step rather than the end of this one. `min-w-0` so the
        // control takes the leftover width instead of overflowing the row.
        <div className="flex items-center gap-3">
          <div className="min-w-0 flex-1">{control}</div>
          {/* Square and the same 40px as the combobox beside it, so the pair
              reads as one control rather than a button parked next to one.
              Icon-only, so it carries its own label. */}
          <Button
            variant="secondary"
            size="md"
            iconLeft={faCheck}
            aria-label="Confirm"
            onClick={onConfirm}
            disabled={confirmDisabled}
            className="w-10 px-0"
          />
        </div>
      ) : (
        control
      )}
      {hasError && (
        <div role="alert">
          <Text variant={TypographyStyles.BODY_SM} color={TextColors.DANGER}>
            This question needs an answer before you can continue.
          </Text>
        </div>
      )}
    </div>
  )
}
